// =============================================================================
// SimSense Platform — Coach Discovery Page (B2B-003)
// =============================================================================
// Students can browse coaches, filter by specialty/price/discipline,
// and open a dedicated coaching profile page with services.
// =============================================================================

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../utils/api";

const DISCIPLINE_FILTERS = ["All", "Oval", "Road", "Dirt Oval", "Dirt Road", "Sports Car", "Open Wheel", "Stock Car", "GT"];

interface CoachCard {
  id: string;
  slug: string;
  brandName: string;
  headline: string;
  specialties: string[];
  supportedDisciplines: string[];
  asyncReviewPriceCents: number;
  hourlyRateCents: number;
  responseSlaHours: number;
  profilePictureUrl?: string;
}

export function CoachDiscovery() {
  const [coaches, setCoaches] = useState<CoachCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [discipline, setDiscipline] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    document.title = "Find a Coach | SimSense";
    async function load() {
      try {
        const data = await api<any[]>("/api/coach-platform/browse");
        setCoaches(data.map((c: any) => ({
          id: c.id,
          slug: c.slug,
          brandName: c.brandName || c.brand_name || c.displayName || "Coach",
          headline: c.headline || "",
          specialties: c.specialties || [],
          supportedDisciplines: c.supportedDisciplines || c.supported_disciplines || [],
          asyncReviewPriceCents: c.asyncReviewPriceCents || c.async_review_price_cents || 0,
          hourlyRateCents: c.hourlyRateCents || c.hourly_rate_cents || 0,
          responseSlaHours: c.responseSlaHours || c.response_sla_hours || 0,
          profilePictureUrl: c.profilePictureUrl || c.profile_picture_url,
        })));
      } catch {
        // API not available yet — show empty state
      }
      setLoading(false);
    }
    load();
  }, []);

  const filtered = coaches.filter(c => {
    if (discipline !== "All" && !c.supportedDisciplines.includes(discipline)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return c.brandName.toLowerCase().includes(q) ||
        c.headline.toLowerCase().includes(q) ||
        c.specialties.some(s => s.toLowerCase().includes(q));
    }
    return true;
  });

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <h2 style={{ fontSize: "22px", marginBottom: "6px" }}>Find a Coach</h2>
        <p style={{ color: "var(--text2)", fontSize: "14px" }}>
          Browse coaching professionals for telemetry reviews, live sessions, and personalized training.
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Search coaches, specialties..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ flex: 1, minWidth: "200px", padding: "10px 14px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: "8px", color: "#fff", fontSize: "13px" }}
        />
        <div style={{ display: "flex", gap: "4px", background: "var(--card)", borderRadius: "8px", border: "1px solid var(--border)", padding: "3px" }}>
          {DISCIPLINE_FILTERS.map(d => (
            <button key={d} onClick={() => setDiscipline(d)} style={{
              padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "pointer", border: "none",
              background: discipline === d ? "var(--accent)" : "transparent",
              color: discipline === d ? "#fff" : "var(--text2)",
            }}>{d}</button>
          ))}
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div style={{ padding: "60px", textAlign: "center", color: "var(--text2)" }}>Loading coaches...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: "60px", textAlign: "center", background: "var(--card)", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
          <div style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>No coaches found</div>
          <p style={{ color: "var(--text3)", fontSize: "13px" }}>
            {coaches.length === 0
              ? "The coaching marketplace is coming soon. Check back later!"
              : "Try adjusting your filters or search terms."}
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "16px" }}>
          {filtered.map(coach => (
            <Link key={coach.id} to={`/coach/${coach.slug}`} style={{ textDecoration: "none", color: "inherit" }}>
              <div style={{
                background: "var(--card)", borderRadius: "12px", border: "1px solid var(--border)", padding: "20px",
                transition: "border-color 0.15s, transform 0.1s", cursor: "pointer",
              }} onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                 onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.transform = "none"; }}>
                {/* Coach header */}
                <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "12px" }}>
                  <div style={{
                    width: "48px", height: "48px", borderRadius: "50%", background: "var(--accent)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: 700, color: "#fff",
                    overflow: "hidden", flexShrink: 0,
                  }}>
                    {coach.profilePictureUrl
                      ? <img src={coach.profilePictureUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : coach.brandName.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "2px" }}>{coach.brandName}</div>
                    <div style={{ fontSize: "12px", color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{coach.headline}</div>
                  </div>
                </div>

                {/* Disciplines */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "12px" }}>
                  {coach.supportedDisciplines.slice(0, 4).map(d => (
                    <span key={d} style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 600, background: "rgba(59,130,246,0.12)", color: "var(--accent)" }}>{d}</span>
                  ))}
                </div>

                {/* Specialties */}
                {coach.specialties.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "12px" }}>
                    {coach.specialties.slice(0, 3).map(s => (
                      <span key={s} style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", background: "rgba(255,255,255,0.05)", color: "var(--text2)" }}>{s}</span>
                    ))}
                  </div>
                )}

                {/* Pricing */}
                <div style={{ display: "flex", gap: "16px", fontSize: "12px", color: "var(--text2)", borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
                  {coach.asyncReviewPriceCents > 0 && (
                    <div>
                      <span style={{ color: "var(--text3)", fontSize: "10px", textTransform: "uppercase" }}>Review</span>
                      <div style={{ fontWeight: 700, color: "#fff" }}>${(coach.asyncReviewPriceCents / 100).toFixed(0)}</div>
                    </div>
                  )}
                  {coach.hourlyRateCents > 0 && (
                    <div>
                      <span style={{ color: "var(--text3)", fontSize: "10px", textTransform: "uppercase" }}>Hourly</span>
                      <div style={{ fontWeight: 700, color: "#fff" }}>${(coach.hourlyRateCents / 100).toFixed(0)}/hr</div>
                    </div>
                  )}
                  {coach.responseSlaHours > 0 && (
                    <div>
                      <span style={{ color: "var(--text3)", fontSize: "10px", textTransform: "uppercase" }}>Response</span>
                      <div style={{ fontWeight: 700, color: "#fff" }}>{coach.responseSlaHours}h</div>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
